import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import {
  AvatarImage,
  avatarImage,
  campaignImage,
  Content,
  Eyebrow,
  PageShell,
  Panel,
  QuotesSection,
  SectionTitle
} from './shared';
import SquadBuilder from './SquadBuilder';
import type {
  CampaignState,
  LeaderboardEntry,
  ReferralSquadData
} from './types';

const BoardPanel = styled(Panel)`
  ${mediaQuery.uptoMobile} {
    padding: 24px 14px 36px;
  }
`;

const BoardIntro = styled.div`
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    gap: 8px;

    h2 {
      margin-top: 0;
      line-height: 1.24;
      width: 310px;
      max-width: 100%;
    }

    img {
      width: 24px;
      height: 24px;
    }
  }
`;

const Hero = styled.section`
  position: relative;
`;

const HeroStage = styled.div`
  position: relative;
  height: 464px;
  overflow: hidden;
  background: #000;

  ${mediaQuery.uptoMobile} {
    height: 190px;
    background: linear-gradient(180deg, #000 0%, #010b41 100%);
  }
`;

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.41;

  ${mediaQuery.uptoMobile} {
    height: 202px;
  }
`;

const Crowd = styled.img`
  position: absolute;
  left: 50%;
  bottom: 16%;
  width: min(720px, 62%);
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 1;

  ${mediaQuery.uptoMobile} {
    left: 52px;
    top: 85px;
    bottom: auto;
    width: 256px;
    height: 105px;
    transform: none;
    object-fit: cover;
    object-position: center 62%;
  }
`;

const SquadLogo = styled.img`
  position: absolute;
  left: 50%;
  top: 28px;
  width: min(441px, 48%);
  transform: translateX(-50%);
  z-index: 2;

  ${mediaQuery.uptoMobile} {
    top: 32px;
    width: 139px;
    height: 49px;
    object-fit: contain;
  }
`;

const Fade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 220px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 15%, #000 80%);
  z-index: 1;

  ${mediaQuery.uptoMobile} {
    height: 94px;
    top: 96px;
    bottom: auto;
  }
`;

const HeroCopy = styled.div`
  position: absolute;
  left: 50%;
  bottom: 28px;
  width: min(876px, calc(100% - 32px));
  transform: translateX(-50%);
  text-align: center;
  z-index: 2;

  ${mediaQuery.uptoMobile} {
    position: relative;
    left: auto;
    bottom: auto;
    transform: none;
    width: 100%;
    padding: 0;
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
`;

const HeroLead = styled.p`
  margin: 0;
  font-size: 36px;
  font-weight: 400;
  line-height: 1.36;
  letter-spacing: 0.02em;

  ${mediaQuery.uptoMobile} {
    font-size: 14px;
    line-height: 1.36;
    letter-spacing: 0.02em;
  }
`;

const HeroTitle = styled.p`
  margin: 6px 0 0;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.17;
  letter-spacing: 0.02em;

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
    font-size: 20px;
    line-height: 1.17;
    letter-spacing: 0.02em;
  }
`;

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
  margin-top: 27px;

  ${mediaQuery.uptoTablet} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 27px;
  }
`;

const RANK_THEME: Record<
  number,
  { border: string; glow: string; ribbon: string }
> = {
  1: {
    border: '#d6ba4b',
    glow: 'inset 0 0 42px rgba(116, 62, 28, 0.9)',
    ribbon: '#e8c14a'
  },
  2: { border: '#7a8cbb', glow: 'inset 0 0 38px #403652', ribbon: '#c5c9d6' },
  3: {
    border: '#622e1a',
    glow: 'inset 0 0 32px rgba(242, 125, 80, 0.33)',
    ribbon: '#c9844a'
  }
};

const DEFAULT_RANK = { border: '#461b20', glow: 'none', ribbon: '#8d7a4d' };

const RankCard = styled.article<{ $rank: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 175px;
  padding: 16px 12px;
  border-radius: 16px;
  background: ${({ $rank }) => ($rank === 2 ? '#170e11' : '#211113')};
  border: 1px solid ${({ $rank }) => (RANK_THEME[$rank] || DEFAULT_RANK).border};
  box-shadow: ${({ $rank }) => (RANK_THEME[$rank] || DEFAULT_RANK).glow};

  ${mediaQuery.uptoMobile} {
    min-height: 175px;
    padding: 16px;
    gap: 18px;

    &:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      width: calc((100% - 12px) / 2);
      justify-self: center;
    }
  }
`;

const Ribbon = styled.span<{ $rank: number }>`
  position: absolute;
  top: 0;
  left: 18px;
  width: 23px;
  height: 37px;
  background: ${({ $rank }) => (RANK_THEME[$rank] || DEFAULT_RANK).ribbon};
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%);
  color: #251c0e;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  padding-top: 4px;

  ${mediaQuery.uptoMobile} {
    left: 18px;
    width: 23px;
    height: 37px;
    font-size: 18px;
  }
`;

const RankMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const RankName = styled.p`
  margin: 18px 0 0;
  font-size: 16px;
  font-weight: 600;

  ${mediaQuery.uptoMobile} {
    margin: 0;
    font-size: 16px;
    text-align: center;
    line-height: 1;
  }
`;

const RankTag = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);

  ${mediaQuery.uptoMobile} {
    margin: 0;
    font-size: 12px;
    text-align: center;
    line-height: 1;
  }
`;

const FriendsPill = styled.p`
  width: 100%;
  margin: auto 0 0;
  padding: 4px 0;
  border-radius: 17px;
  background: #120305;
  border: 1px solid #321c1f;
  text-align: center;
  font-size: 14px;

  strong {
    font-weight: 700;
  }

  ${mediaQuery.uptoMobile} {
    font-size: 14px;
  }
`;

type SquadScreenProps = {
  data: CampaignState['squad'];
  onSquadUpdate?: (data: ReferralSquadData) => void;
};

const SquadScreen = ({ data, onSquadUpdate }: SquadScreenProps) => (
  <PageShell>
    <Hero>
      <HeroStage>
        <HeroBg src={campaignImage('squad-bg.png')} alt="" />
        <Crowd src={campaignImage('squad-crowd.png')} alt="" />
        <Fade />
        <SquadLogo
          src={campaignImage('squad-logo.png')}
          alt="Build your squad"
        />
      </HeroStage>
      <HeroCopy>
        <HeroLead>Group chat ko mute mat karo, bestie.</HeroLead>
        <HeroTitle>It&apos;s about to become relevant.</HeroTitle>
      </HeroCopy>
    </Hero>
    <Content $afterHero="squad">
      <SquadBuilder
        members={data.members}
        extraCount={data.extraCount}
        promotionPart={2}
        onSquadUpdate={onSquadUpdate}
      />
      {data.leaderboard.length > 0 ? (
        <BoardPanel>
          <BoardIntro>
            <Eyebrow>
              <img
                src={campaignImage('poll-icon.svg')}
                alt=""
                width={24}
                height={24}
              />
              Squad leaderboard
            </Eyebrow>
            <SectionTitle>
              Squad badi, chances bade. Top 5 mein aao, prize uthao. 🏆
            </SectionTitle>
          </BoardIntro>
          <BoardGrid>
            {data.leaderboard.map((entry: LeaderboardEntry) => (
              <RankCard key={entry.id} $rank={entry.rank}>
                <Ribbon $rank={entry.rank}>{entry.rank}</Ribbon>
                <AvatarImage src={avatarImage(entry.avatarIndex)} alt="" />
                <RankMeta>
                  <RankName>{entry.name}</RankName>
                  {entry.tag ? <RankTag>{entry.tag}</RankTag> : null}
                </RankMeta>
                <FriendsPill>
                  <strong>{entry.friends}</strong> friends
                </FriendsPill>
              </RankCard>
            ))}
          </BoardGrid>
        </BoardPanel>
      ) : null}
      <QuotesSection quotes={data.quotes} />
    </Content>
  </PageShell>
);

export default SquadScreen;
